import { Component, OnInit, Input, OnDestroy } from "@angular/core";
import { Validators, UntypedFormBuilder, FormGroup } from "@angular/forms";
import { ProfileService } from "src/app/api/profile.service";
import { User } from "src/app/models/user";
import { SupportService } from "src/app/api/support.service";
import { SupportCategory, SupportInput } from "src/app/models/support";
import { HCaptchaService } from "src/app/services/hcaptcha.service";
import { AuthService } from "src/app/api/auth.service";
import { Subject, takeUntil } from "rxjs";
import { ActivatedRoute } from "@angular/router";
import { LocaleService } from "src/app/services/locale.service";

@Component({
  selector: "support-form",
  templateUrl: "./support-form.component.html",
  styleUrls: ["./support-form.component.scss"],
})
export class SupportFormComponent implements OnInit, OnDestroy {
  @Input() type: string = "";
  @Input() title: string = "";
  @Input() isTypeCopperateOrVip: boolean = false;
  private destroy$ = new Subject<void>();

  loading = false;
  loadingCaptcha = false;
  error: string = "";
  success = false;

  form = this.fb.group({
    main: this.fb.group({
      category: ["", Validators.required],
      subCategory: ["", Validators.required],
    }),
    ticketData: this.fb.group({
      email: [
        { value: "", disabled: this.isLoggedIn() },
        [Validators.required, Validators.email],
      ],
    }),
  });
  categories: SupportCategory[] = [];
  user: User | null = null;

  constructor(
    private localeService: LocaleService,
    private fb: UntypedFormBuilder,
    private profileSerivce: ProfileService,
    private supportService: SupportService,
    private hCaptchaService: HCaptchaService,
    private auth: AuthService,
    private route: ActivatedRoute,
  ) {
    this.type = this.route.snapshot.paramMap.get("type") ?? "";
  }

  ngOnInit() {
    this.profileSerivce
      .get()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: User) => {
          this.user = response;
          this.email?.setValue(this.user?.email);
        },
        (err) => {},
      );

    this.getCategories();
  }

  getCategories() {
    this.supportService
      .getCategories()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: SupportCategory[]) => {
        this.categories = response;
        for (let category of this.categories) {
          if (category.key == "vipUserSupport" && this.isTypeVip())
            this.category?.setValue(category);
          if (category.key == "otc" && this.isTypeOtc())
            this.category?.setValue(category);
        }
        this.applyQueryParams();
      });
  }

  applyQueryParams() {
    const categoryKey = this.route.snapshot.queryParamMap.get("category");
    const subcategoryKey = this.route.snapshot.queryParamMap.get("subcategory");

    if (!categoryKey || !this.categories) return;

    const matchedCategory = this.categories.find((c) => c.key === categoryKey);
    if (!matchedCategory) return;

    this.category?.setValue(matchedCategory);

    if (!subcategoryKey) return;

    const matchedSubcategory = matchedCategory.subCategories?.find(
      (s) => s.key === subcategoryKey,
    );
    if (matchedSubcategory) {
      this.subCategory?.setValue(matchedSubcategory);
    }
  }

  resetForm() {
    this.clearTicketDataControls();
    this.subCategory?.reset();
    this.category?.reset();

    if (this.isLoggedIn()) {
      this.email?.setValue(this.user?.email);
    } else {
      this.email?.reset();
    }
  }

  onSubmit() {
    this.success = false;
    this.error = "";
    if (this.form.valid) {
      const payload: SupportInput = {} as SupportInput;
      Object.keys(this.ticketData.controls).forEach((key: string) => {
        (payload as Record<string, any>)[key] =
          this.ticketData.controls[key].value;
      });
      this.sendTicket(payload);
    } else {
      this.form.markAllAsTouched();
      if (this.localeService.isArabic())
        this.error = "الرجاء تعبئة كافة الحقول المطلوبة.";
      else this.error = "Please fill out the required fields";
      this.scrollToTop();
    }
  }

  createFinalPayload(payloadParams: string[]): SupportInput {
    const payload: SupportInput = {} as SupportInput;
    for (const param of payloadParams) {
      (payload as Record<string, any>)[param] = this.form.get(param)?.value;
    }
    return payload;
  }

  async sendTicket(payload: SupportInput) {
    this.loading = true;
    let captcha: string = "";

    if (this.hCaptchaService.isCaptchaEnabled) {
      this.loadingCaptcha = true;

      try {
        captcha = await this.hCaptchaService.getCaptcha();
      } catch (err) {
        console.log("hcaptcha_ERROR", err);
        return;
      } finally {
        this.loadingCaptcha = false;
      }
    }

    this.supportService
      .send(this.subCategory?.value?.key, payload, captcha)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          this.loading = false;

          if (response) {
            this.success = true;
          } else {
            if (this.localeService.isArabic())
              this.error = "حصل خطأ، الرجاء المحاولة لاحقاً.";
            else this.error = "Something went wrong, please try again later";
          }

          this.scrollToTop();
          this.resetForm();
        },
        (err) => {
          this.loading = false;
          this.error =
            err?.errors?.[0]?.message || err?.error?.error_description || "";
          this.scrollToTop();
          this.resetForm();
        },
      );
  }

  scrollToTop() {
    window.scroll(0, 0);
  }

  isDocumentTicket(category: String): boolean {
    if (category == "documentRenewal" || category == "documentUploadError")
      return true;
    return false;
  }

  isTechnicalIssueTicket(category: String): boolean {
    if (
      category == "followUpOnVerificationRequest" ||
      category == "buySellIssueErrorPlacingOrder" ||
      category == "buySellIssueOtherRelatedIssue" ||
      category == "buyOrSellIssue" ||
      category == "appIssue" ||
      category == "websiteIssue" ||
      category == "technicalOther"
    )
      return true;
    return false;
  }

  isDepositTicket(category: String): boolean {
    if (category == "easyDelayedDeposit" || category == "onlineDelayedDeposit")
      return true;
    return false;
  }

  isNameTicket(category: String): boolean {
    if (category == "legalNameChange" || category == "nameMismatchCorrection")
      return true;
    return false;
  }

  isOtpTicket(category: String): boolean {
    if (category == "enableTheOtpSms") return true;
    return false;
  }

  isPhoneTicket(category: String): boolean {
    if (category == "changeLinkedPhoneNumber") return true;
    return false;
  }

  isBankTicket(category: String): boolean {
    if (category == "changeBankAccountDetails") return true;
    return false;
  }

  isLoggedIn(): boolean {
    if (this.auth.getToken()) return true;
    return false;
  }

  isEmailChangeTicket(category: string): boolean {
    return category == "changeRegisteredEmailAddress";
  }

  isResidentialChangeTicket(category: string): boolean {
    return category == "changeResidentialAddressChange";
  }

  isGeneralTicket(category: string) {
    if (
      !this.isBankTicket(category) &&
      !this.isPhoneTicket(category) &&
      !this.isOtpTicket(category) &&
      !this.isNameTicket(category) &&
      !this.isDepositTicket(category) &&
      !this.isTechnicalIssueTicket(category) &&
      !this.isDocumentTicket(category) &&
      !this.isEmailChangeTicket(category) &&
      !this.isResidentialChangeTicket(category)
    )
      return true;
    return false;
  }

  changeCategory() {
    this.clearTicketDataControls();
    this.subCategory?.reset();
  }

  changeSubCategory() {
    this.clearTicketDataControls();
  }

  clearTicketDataControls() {
    Object.keys(this.ticketData.controls).forEach((control: string) => {
      if (control !== "email") {
        this.ticketData.removeControl(control);
      }
    });
  }

  isTypeVip() {
    return this.type === "vipUserSupport";
  }

  isTypeOtc() {
    return this.type === "otc";
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get email() {
    return this.form.get("ticketData.email");
  }

  get category() {
    return this.form.get("main.category");
  }

  get subCategory() {
    return this.form.get("main.subCategory");
  }

  get ticketData() {
    return this.form.get("ticketData") as FormGroup;
  }
}
